import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { sendInviteEmail } from '../../lib/email';
import './AddClient.css';

export default function AddClient() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteResult, setInviteResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get practitioner record
      const { data: prac, error: pracError } = await supabase
        .from('practitioners')
        .select('id')
        .eq('auth_user_id', user.id)
        .single();

      if (pracError || !prac) {
        setError('Could not find your practitioner account.');
        setLoading(false);
        return;
      }

      // Create client record (invite_code is auto-generated by the database)
      const { data: newClient, error: insertError } = await supabase
        .from('clients')
        .insert({
          practitioner_id: prac.id,
          name,
          email,
        })
        .select()
        .single();

      if (insertError) {
        setError(insertError.message);
        setLoading(false);
        return;
      }

      // Build the invite link
      const baseUrl = window.location.origin;
      const inviteLink = `${baseUrl}/signup/${newClient.invite_code}`;

      // Get practitioner name for the email
      const { data: pracData } = await supabase
        .from('practitioners')
        .select('name, practice_name')
        .eq('id', prac.id)
        .single();

      const practitionerName = pracData?.name || 'Your practitioner';

      // Send invite email to the client
      const emailResult = await sendInviteEmail({
        clientName: name,
        clientEmail: email,
        practitionerName,
        inviteCode: newClient.invite_code,
        inviteLink,
      });

      setInviteResult({
        client: newClient,
        inviteLink,
        inviteCode: newClient.invite_code,
        emailSent: emailResult.success,
        emailError: emailResult.error,
      });
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }

    setLoading(false);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
  };

  // Success state — show invite link
  if (inviteResult) {
    return (
      <div className="screen container">
        <div className="add-client-success">
          <div className="success-icon">✓</div>
          <h2>Client Added</h2>
          <p className="success-name">{inviteResult.client.name}</p>

          {inviteResult.emailSent ? (
            <div className="email-sent-badge">
              <span className="email-sent-icon">✉</span>
              <span>Invite email sent to <strong>{inviteResult.client.email}</strong></span>
            </div>
          ) : (
            <p className="success-desc">
              {inviteResult.emailError
                ? `Email couldn\u2019t be sent (${inviteResult.emailError}). Share the invite link below manually.`
                : 'Share this invite link with your client so they can create their account and connect to your practice.'}
            </p>
          )}

          <p className="success-desc" style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
            You can also share the link or code below directly.
          </p>

          <div className="invite-link-box">
            <label>Invite Link</label>
            <div className="invite-link-row">
              <input
                type="text"
                readOnly
                value={inviteResult.inviteLink}
                className="invite-link-input"
              />
              <button
                onClick={() => copyToClipboard(inviteResult.inviteLink)}
                className="copy-btn"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="invite-link-box" style={{ marginTop: '12px' }}>
            <label>Invite Code</label>
            <div className="invite-link-row">
              <input
                type="text"
                readOnly
                value={inviteResult.inviteCode}
                className="invite-link-input"
                style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
              />
              <button
                onClick={() => copyToClipboard(inviteResult.inviteCode)}
                className="copy-btn"
              >
                Copy
              </button>
            </div>
          </div>

          <div className="success-actions">
            <button
              onClick={() => {
                setInviteResult(null);
                setName('');
                setEmail('');
              }}
              className="btn btn-secondary"
            >
              Add Another Client
            </button>
            <button
              onClick={() => navigate('/practitioner')}
              className="btn btn-primary"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen container">
      <button onClick={() => navigate(-1)} className="back-link">
        ← Back
      </button>

      <h1 className="add-client-title">Add Client</h1>
      <p className="add-client-desc">
        Enter your client's details. They'll receive an invite code to create their account.
      </p>

      <form className="add-client-form" onSubmit={handleSubmit}>
        {error && <div className="auth-error">{error}</div>}

        <div className="form-group">
          <label htmlFor="client-name">Client Name</label>
          <input
            id="client-name"
            type="text"
            className="form-input"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="client-email">Email</label>
          <input
            id="client-email"
            type="email"
            className="form-input"
            placeholder="client@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '8px' }} disabled={loading}>
          {loading ? 'Adding Client...' : 'Add Client & Generate Invite'}
        </button>
      </form>
    </div>
  );
}
